/* eslint-disable camelcase */
import { extend, localize } from 'vee-validate'
import {
  required as rule_required,
  email as rule_email,
  min as rule_min,
  confirmed as rule_confirmed,
  regex as rule_regex,
  between as rule_between,
  alpha as rule_alpha,
  integer as rule_integer,
  digits as rule_digits,
  alpha_dash as rule_alpha_dash,
  alpha_num as rule_alpha_num,
  length as rule_length,
} from 'vee-validate/dist/rules'
// import ar from 'vee-validate/dist/locale/ar.json'
// import en from 'vee-validate/dist/locale/en.json'
import fr from 'vee-validate/dist/locale/fr.json'

// eslint-disable-next-line object-curly-newline
import {
  createAsyncEmailValidator,
  createAsyncPhoneValidator,
  createAsyncUsernameValidator,
} from '@/utils/asyncValidators'
import {
  validatorPositive, validatorUrlValidator, validatorPassword, validatorCreditCard,
} from './validators'

// ////////////////////////////////////////////////////////
// General
// ////////////////////////////////////////////////////////

export const required = extend('required', rule_required)

export const email = extend('email', rule_email)

export const min = extend('min', rule_min)

export const confirmed = extend('confirmed', rule_confirmed)

export const regex = extend('regex', rule_regex)

export const between = extend('between', rule_between)

export const alpha = extend('alpha', rule_alpha)

export const integer = extend('integer', rule_integer)

export const digits = extend('digits', rule_digits)

export const alphaDash = extend('alpha-dash', rule_alpha_dash)

export const alphaNum = extend('alpha-num', rule_alpha_num)

export const length = extend('length', rule_length)

extend('length', {
  ...rule_length,
  message: 'Le champ {_field_} doit contenir {length} caractères.', // custom error message in French
})

extend('required', {
  ...rule_required,
  message: 'Le champ {_field_} est obligatoire',
})

export const positive = extend('positive', {
  validate: validatorPositive,
  message: 'Veuillez saisir un nombre positif !',
})

export const credit = extend('credit-card', {
  validate: validatorCreditCard,
  message: 'It is not valid credit card!',
})

export const password = extend('password', {
  validate: validatorPassword,
  message: 'votre {_field_} doit contenir au moins une majuscule, une minuscule, un caractère spécial et un chiffre',
})

export const url = extend('url', {
  validate: validatorUrlValidator,
  message: "L'URL n'est pas valide",
})

// Activate French localization globally and merge any custom names/fields.
localize('fr', {
  messages: fr.messages,
  names: {
    email: 'Email',
    password: 'Mot de passe',
  },
  fields: {
    password: {
      min: '{_field_} est trop court',
    },
  },
})
// ////////////////////////////////////////////////////////
// NOTE:
// Quasar validation for reference only
// Remove this note once development is finished and make sure to
// to convert all of them in veevalidate version
// ////////////////////////////////////////////////////////

// export const required = (val) => {
//   return (val && val.length > 0) || '*Field is required'
// }

// export const required_obj = (obj) => {
//   if (obj === null || obj === undefined) return '*Field is required'
//   return (Object.entries(obj).length > 0 && obj.constructor === Object) || '*Field is required'
// }

// export const no_blank_spaces_arr = (arr) => {
//   return arr.every(val => (val.trim() && val.trim().length > 0)) || 'Blank Spaces are not allowed'
// }

// export const url = val => {
//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   // Used
//   // https://stackoverflow.com/questions/4314741/url-regex-validation

//   // Other
//   // https://stackoverflow.com/questions/5717093/check-if-a-javascript-string-is-a-url
//   // https://www.w3resource.com/javascript-exercises/javascript-regexp-exercise-9.php
//   // https://www.geeksforgeeks.org/how-to-validate-url-using-regular-expression-in-javascript/

//   /* eslint-disable no-useless-escape */
//   const re = /^(http[s]?:\/\/){0,1}(www\.){0,1}[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,5}[\.]{0,1}/
//   /* eslint-enable no-useless-escape */
//   return re.test(val) || 'URL is invalid'
// }

// export const date = val => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   // https://github.com/quasarframework/quasar/blob/dev/ui/src/utils/patterns.js
//   return /^-?[\d]+\/[0-1]\d\/[0-3]\d$/.test(val) || 'Date is invalid'
// }

// export const max = (val, max) => {

//   // If blank return
//   if (val === undefined || val === null) return true

//   return val.length <= max || `More than ${max} characters are not allowed`
// }

// export const max_arr = (val, max) => {
//   return val.length <= max || `More than ${max} values are not allowed`
// }

// export const min = (val, min) => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   return val.length >= min || `Minimum ${min} characters are required`
// }

// export const num_range = (val, min, max) => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   const msg = 'Value is invalid'
//   if (min === null) return val <= max || msg
//   else if (max === null) return val >= min || msg
//   else return (val >= min && val <= max) || msg
// }

// ////////////////////////////////////////////////////////
// Async Validation Rules (Real-time uniqueness checks)
// ////////////////////////////////////////////////////////

/**
 * Async Email Uniqueness Validator
 * Usage in template:
 * rules="required|email|asyncEmail"
 * or with exclude for edit forms:
 * rules="required|email|asyncEmail:123"
 */
export const asyncEmail = extend('asyncEmail', {
  validate: async (value, { excludeUserId }) => {
    const validator = createAsyncEmailValidator(excludeUserId || null)
    return validator(value)
  },
  params: ['excludeUserId'],
  message: 'Cet email est déjà utilisé.',
})

/**
 * Async Phone Uniqueness Validator
 * Usage in template:
 * rules="required|tnPhone|asyncPhone"
 * or with company and exclude for edit forms:
 * rules="required|tnPhone|asyncPhone:1,123"
 */
export const asyncPhone = extend('asyncPhone', {
  validate: async (value, { companyId, excludeUserId }) => {
    const validator = createAsyncPhoneValidator(companyId || null, excludeUserId || null)
    return validator(value)
  },
  params: ['companyId', 'excludeUserId'],
  message: 'Ce numéro de téléphone est déjà utilisé.',
})

/**
 * Async Username Uniqueness Validator
 * Usage in template:
 * rules="required|asyncUsername"
 * or with exclude for edit forms:
 * rules="required|asyncUsername:123"
 */
export const asyncUsername = extend('asyncUsername', {
  validate: async (value, { excludeUserId }) => {
    const validator = createAsyncUsernameValidator(excludeUserId || null)
    return validator(value)
  },
  params: ['excludeUserId'],
  message: 'Ce nom d\'utilisateur est déjà utilisé.',
})
